import CryptoJS from "crypto-js";
import moment from "moment";

import { AlertType } from "../provider/AlertProvider";

import * as Common from "../commons/common";
import * as Types from "../commons/typeDTO";
import * as Strings from "../commons/string";

const secretPass = "RUTA40";
export const getLocalStorage = (key: string) => {
    const item = localStorage.getItem(key);
    if (!item) return item;

    const decrypted = CryptoJS.AES.decrypt(item, secretPass);

    try {
        const converted = decrypted.toString(CryptoJS.enc.Utf8);
        return converted;
    } catch (error) {
        removeLocalStorage(key);
        return undefined;
    }
};

export const setLocalStorage = (key: string, value: string) => {
    const encryped = CryptoJS.AES.encrypt(value, secretPass).toString();
    return localStorage.setItem(key, encryped);
};

export const removeLocalStorage = (key: string) => {
    return localStorage.removeItem(key);
};

export const utcToLocalTime = (utcTime: string, format: string) => {
    const localTime = moment.utc(utcTime).toDate();
    return moment(localTime).format(format);
};

export const checkUnAuthorizedToken = (error: any | null) => {
    if (!error) return false;

    if (error.response.data.code === AlertType.UNAUTHORIZED && error.response.data.message === "Unauthorized token") {
        return true;
    }

    return false;
};

export const isPossibleAutoLogin = () => {
    const savedEmail = getLocalStorage(Common.CONTEXT_SAVED_EMAIL);
    const savedPassword = getLocalStorage(Common.CONTEXT_SAVED_PASSWORD);
    if (savedEmail && savedEmail.length > 0 && savedPassword && savedPassword.length) {
        return { isPossible: true, email: savedEmail, password: savedPassword };
    }

    return { isPossible: false };
};

export type RentsByDate = {
    [key: string]: Array<Types.RentDto>;
};

export const convertRentByStartDate = (rents: Array<Types.RentDto>, isShowAdminReservation: boolean) => {
    const obj: RentsByDate = {};
    rents.forEach((rent) => {
        if (rent.rentStatus !== "CANCELED" && (isShowAdminReservation || rent.user.email !== "support@alpamotors.com")) {
            if (!obj[rent.startDate]) obj[rent.startDate] = [];
            obj[rent.startDate].push(rent);
        }
    });

    return obj;
};

export const convertRentByEndDate = (rents: Array<Types.RentDto>, isShowAdminReservation: boolean) => {
    const obj: RentsByDate = {};
    rents.forEach((rent) => {
        if (rent.rentStatus !== "CANCELED" && (isShowAdminReservation || rent.user.email !== "support@alpamotors.com")) {
            if (!obj[rent.endDate]) obj[rent.endDate] = [];
            obj[rent.endDate].push(rent);
        }
    });

    return obj;
};

export const TypeRentStatus = {
    DURING_PAYMENT: "결제중",
    RESERVED: "예약완료",
    CANCELED: "예약취소",
    IN_USE: "이용중",
    ARRIVED: "도착완료",
    COMPLETED: "반납완료",
    NO_SHOW: "노쇼",
};

export const getRentStatus = (rent: Types.RentDto) => {
    return TypeRentStatus[rent.rentStatus];
};

export const isPossibleCancel = (rent: Types.RentDto) => {
    return rent.rentStatus === "RESERVED";
};

export const getPaidItemsString = (rent: Types.RentDto) => {
    let paidItemsString = "";

    rent.rentPaidItems.forEach((item) => (paidItemsString += item.rentPaidItemName + "\n"));
    if (paidItemsString.length === 0) paidItemsString = Strings.dash;

    return paidItemsString;
};

export const convertDateString = (date: string | undefined | null, format: string) => {
    return date ? moment(date).format(format) : Strings.dash;
};

export const convertCurrency = (price: number) => {
    return new Intl.NumberFormat("ko-KR").format(price);
};

export const convertCurrencyKorean = (price: number) => {
    return convertCurrency(price) + "원";
};

export const onEvent = (eventType: string, listener: (event?: any) => void) => {
    document.addEventListener(eventType, listener);
};

export const offEvent = (eventType: string, listener: (event?: any) => void) => {
    document.removeEventListener(eventType, listener);
};

export const onceEvent = (eventType: string, listener: (event?: any) => void) => {
    onEvent(eventType, handleEventOnce);

    function handleEventOnce() {
        listener();
        offEvent(eventType, handleEventOnce);
    }
};

export const triggerEvent = (eventType: string, data?: any) => {
    document.dispatchEvent(new CustomEvent(eventType, { detail: data }));
};

export const initModalStack = () => {
    sessionStorage.setItem(Common.SESSION_SHOW_MODAL, String(0));
};

export const addModalStack = () => {
    let currStack = getModalStack();
    currStack++;
    sessionStorage.setItem(Common.SESSION_SHOW_MODAL, String(currStack));
};

export const popModalStack = () => {
    let currStack = getModalStack();
    currStack--;
    if (currStack < 0) currStack = 0;
    sessionStorage.setItem(Common.SESSION_SHOW_MODAL, String(currStack));
};

export const getModalStack = () => {
    return Number(sessionStorage.getItem(Common.SESSION_SHOW_MODAL));
};

export const initDrawerStack = () => {
    sessionStorage.setItem(Common.SESSION_SHOW_DRAWER, String(0));
};

export const addDrawerStack = () => {
    let currStack = getDrawerStack();
    currStack++;
    sessionStorage.setItem(Common.SESSION_SHOW_DRAWER, String(currStack));
};

export const popDrawerStack = () => {
    let currStack = getDrawerStack();
    currStack--;
    if (currStack < 0) currStack = 0;
    sessionStorage.setItem(Common.SESSION_SHOW_DRAWER, String(currStack));
};

export const getDrawerStack = () => {
    return Number(sessionStorage.getItem(Common.SESSION_SHOW_DRAWER));
};

export const getPaymentItemAmount = (rent: Types.RentDto, paymentItem: Types.RentPaymentItemDto) => {
    if (paymentItem.amount !== 0) return paymentItem.amount;

    const endDayOfWeek = moment(rent?.endDate).weekday();
    const penaltyAmount =
        endDayOfWeek === 5 || endDayOfWeek === 6
            ? rent?.rentCar.weekendDiscountPrice
                ? rent?.rentCar.weekendDiscountPrice
                : rent?.rentCar.weekendPrice
            : rent?.rentCar.discountPrice
            ? rent?.rentCar.discountPrice
            : rent?.rentCar.price;

    if (!penaltyAmount) return 0;

    if (paymentItem.memo === "반납일 정상가 요금 100%") {
        return penaltyAmount;
    } else if (paymentItem.memo === "반납일 정상가 요금 200%") {
        return penaltyAmount * 2;
    }
    return 0;
};

export const convertPaymentMethod = (method: string) => {
    let convertMethod = "";
    switch (method) {
        case "OFFLINE_CARD":
            convertMethod = Strings.offline_card;
            break;
        case "OFFLINE_BANK":
            convertMethod = Strings.offline_bank;
            break;
        case "OFFLINE_CASH":
            convertMethod = Strings.offline_cash;
            break;
        default:
            break;
    }

    return convertMethod;
};

export const convertSubBatteryStatus = (status: "NORMAL" | "LOW" | "VERY_LOW") => {
    let convetString = "";
    switch (status) {
        case "NORMAL": {
            convetString = "정상";
            break;
        }

        case "LOW": {
            convetString = "방전 주의";
            break;
        }

        case "VERY_LOW": {
            convetString = "방전 위험";
            break;
        }

        default:
            break;
    }

    return convetString;
};

export const validateEmail = (isFullType: boolean, hasRequired: boolean, value: string) => {
    const emailPattern = /^[^@]+@[^@]+\.[a-zA-Z]{2,}$/;
    const localPart = value.split("@")[0] || "";
    const domainPart = value.split("@")[1] || "";

    if (!value || !emailPattern.test(value) || localPart.length === 0 || domainPart.length === 0) {
        if (!hasRequired || value) {
            return Promise.reject(new Error("이메일을 입력해 주세요."));
        }
    }

    const localPartPattern = /^(?!\.)(?!.*\.\.)(?!.*\.$)[a-z0-9!#$%&'*+/=?^_`{}|~.-]+$/i;
    const domainPartPattern = /^(|[a-z0-9]([a-z0-9.-]*[a-z0-9])?)$/i;

    if (!localPartPattern.test(localPart) || !domainPartPattern.test(domainPart) || value.includes("..") || value.startsWith(".") || value.endsWith(".")) {
        return Promise.reject(new Error("영문자, 숫자 및 일부 특수문자만 입력 가능합니다."));
    }

    if (!isFullType) {
        if (localPart.length > 64) {
            return Promise.reject(new Error("@ 앞 아이디 부분은 최대 64자까지 입력할 수 있습니다."));
        }

        if (domainPart.length > 190) {
            return Promise.reject(new Error("@ 이후 도메인 값은 최대 190자까지 입력할 수 있습니다."));
        }
    }

    if (value.length > 320) {
        return Promise.reject(new Error("최대 320자까지 입력할 수 있습니다."));
    }

    return Promise.resolve();
};
